import React from 'react';
import { getActivityStats } from '../../activities/activityTypes';
import {
  FIELD_KEYS,
  pick,
  DetailTopBar,
  DetailHero,
  DetailTitleSheet,
  WeatherPlaceholder,
} from './ActivityDetailPage';

// Escalada-specific field keys (on top of the shared ones).
const ESCALADA_KEYS = {
  sector: ['sector', 'zona', 'subzona'],
  orientation: ['orientation', 'orientacion'],
};

/**
 * Escalada detail page. Follows the v3 "02 · Escalada" design: cota belt, hero,
 * beveled title, description and a grade grid (UIAA / Francés / Largos /
 * Equipamiento) plus the weather strip. The croquis of pitches isn't
 * implemented yet, so its column is replaced by a "coming soon" placeholder.
 * Any datum absent from the activity is omitted; the weather is a placeholder.
 */
function EscaladaDetail({ activity }) {
  const values = activity.values || {};
  const description = pick(values, FIELD_KEYS.description);
  const place = pick(values, FIELD_KEYS.location);
  const sector = pick(values, ESCALADA_KEYS.sector);
  const gpx = pick(values, FIELD_KEYS.gpx);

  const stats = getActivityStats(activity).filter((s) => s.present);

  return (
    <div className="Detail">
      <DetailTopBar
        activity={activity}
        rightCell={gpx && <span className="Detail__barCell Detail__barCell--gpx">GPX</span>}
      />

      <DetailHero activity={activity} />

      <DetailTitleSheet
        eyebrow={[sector && String(sector).toUpperCase(), place && String(place).toUpperCase()]
          .filter(Boolean)
          .join(' · ')}
        title={activity.name}
      />

      <div className="Detail__body Detail__body--single">
        <div className="Detail__col">
          {description && (
            <section className="Detail__section">
              <div className="Detail__sectionLabel">DESCRIPCIÓN</div>
              {String(description)
                .split(/\n{2,}/)
                .map((para, i) => (
                  <p key={i} className="Detail__desc">{para}</p>
                ))}
            </section>
          )}

          {stats.length > 0 && (
            <div className="Detail__stats Detail__stats--wide">
              {stats.map((stat) => {
                // The first tile (UIAA) is the key datum, highlighted in amber.
                const accent = stat.unit === 'UIAA';
                return (
                  <div
                    key={stat.unit}
                    className={`Detail__stat${accent ? ' Detail__stat--accent' : ''}`}
                  >
                    <span className="Detail__statLabel">
                      {stat.unit === 'Equipada' ? 'EQUIPAMIENTO' : stat.unit.toUpperCase()}
                    </span>
                    {stat.kind === 'bool' ? (
                      <span className="Detail__statEquip">
                        <span
                          className="Detail__equipDot"
                          style={{ background: stat.value ? 'var(--flik-status-open)' : 'var(--flik-status-closed)' }}
                          aria-hidden="true"
                        />
                        {stat.value ? 'Equipada' : 'No equipada'}
                      </span>
                    ) : (
                      <span className="Detail__statVal">{stat.value}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Croquis de largos: sin datos todavía → no se muestra nada. */}

          <WeatherPlaceholder />
        </div>
      </div>

      {gpx && (
        <a className="Detail__gpxFooter" href={gpx} download>
          <span className="Detail__gpxFooterText">
            <span>SIGUIENTE PASO</span>
            <strong>DESCARGAR GPX</strong>
          </span>
          <span className="Detail__gpxFooterIcon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="butt">
              <path d="M12 4 L12 16 M6 11 L12 17 L18 11 M4 20 L20 20" />
            </svg>
          </span>
        </a>
      )}
    </div>
  );
}

export default EscaladaDetail;
